import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Clock, Truck, CheckCircle2, ArrowRight, Quote, User, Utensils, ChefHat, Sparkles } from 'lucide-react';
import axios from '../api/axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import GridBg from '../assets/grid.svg';
import NoiseBg from '../assets/noise.svg';

const Landing = () => {
  const navigate = useNavigate();
  const [featuredItems, setFeaturedItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // For Marquee
  const [events, setEvents] = useState([]); // For (Events)
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState([
    { number: '500+', label: 'Happy Customers' },
    { number: '1000+', label: 'Orders Delivered' },
    { number: '50+', label: 'Menu Items' },
    { number: '4.8', label: 'Average Rating' }
  ]);

  const features = [
    {
      icon: <Clock className="w-8 h-8 text-gold-500" />,
      title: 'Fast Delivery',
      description: 'Hot and fresh within 30-45 minutes'
    },
    {
      icon: <CheckCircle2 className="w-8 h-8 text-gold-500" />,
      title: 'Quality Food',
      description: 'Fresh ingredients & expert chefs'
    },
    {
      icon: <Truck className="w-8 h-8 text-gold-500" />,
      title: 'Wide Coverage',
      description: 'Delivering across the city'
    },
    {
      icon: <Star className="w-8 h-8 text-gold-500" />,
      title: 'Top Rated',
      description: 'Trusted by hundreds'
    }
  ];

  useEffect(() => {
    fetchFeaturedItems();
    fetchReviews();
    fetchReviewStats();
    fetchEvents();
  }, []);

  const fetchFeaturedItems = async () => {
    try {
      const response = await axios.get('/menu');
      const data = response.data;
      const items = Array.isArray(data) ? data : (data.menuItems || []);
      const availableItems = items.filter(item => item.is_available === true || item.is_available === 1);
      setFeaturedItems(availableItems.slice(0, 4));
      setAllItems(availableItems); // Set all items for marquee
    } catch (error) {
      logger.error('Error fetching featured items:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events'); // Public active events
      setEvents(response.data);
    } catch (error) {
      logger.error('Error fetching events:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get('/reviews');
      const topReviews = response.data.filter(r => r.rating === 5).slice(0, 3);
      setReviews(topReviews);
    } catch (error) {
      logger.error('Error fetching reviews:', error);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const response = await axios.get('/reviews/stats');
      setStats(prev => prev.map((stat, index) =>
        index === 3 ? { ...stat, number: response.data.average.toString() } : stat
      ));
    } catch (error) {
      logger.error('Error fetching review stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-hidden relative">
      <div
        className="fixed inset-0 bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"
        style={{ backgroundImage: `url(${GridBg})` }}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#050505] to-black z-0" />
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          >
            <source src="/videos/hero-bg.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/40 to-black z-0" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gold-500/10 rounded-full blur-[120px] animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[120px] animate-pulse-slow delay-1000" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative inline-block mb-12 group animate-float"
          >
            <div className="absolute inset-0 bg-gold-500 rounded-full blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse-slow"></div>
            <img
              src="/logo.jpg"
              alt="Magic World Logo"
              className="w-48 h-48 object-cover rounded-full border-4 border-gold-500/50 relative z-10 shadow-neon-gold"
            />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-6xl md:text-9xl font-bold mb-6 font-serif bg-clip-text text-transparent bg-gradient-to-r from-gold-200 via-gold-500 to-gold-200 tracking-tighter drop-shadow-lg"
          >
            Magic World
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-2xl md:text-4xl text-gold-100/60 mb-8 font-light tracking-[0.2em] uppercase"
          >
            Premium Cloud Kitchen
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light"
          >
            Experience the magic of handcrafted flavors delivered to your doorstep.
            <br /><span className="text-gold-400 font-medium">Fresh, Delicious, Divine.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <Button
              onClick={() => navigate('/menu')}
              size="lg"
              className="bg-gold-500 text-black hover:bg-gold-400 font-bold text-lg px-8 py-6 rounded-full shadow-lg shadow-gold-500/20 hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Order Now
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })} // Simple scroll
              className="border-gold-500 text-gold-500 hover:bg-gold-500/10 font-bold text-lg px-8 py-6 rounded-full hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              Learn More
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-16 flex justify-center gap-8 md:gap-16 text-gray-400 text-sm md:text-base"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gold-500" />
              <span>30-45 min delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-gold-500" />
              <span>4.8/5 Rating</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Infinite Menu Marquee */}
      {allItems.length > 0 && (
        <div className="w-full bg-black py-4 border-y border-gold-500/20 overflow-hidden relative z-20">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />

          <div className="w-full flex overflow-hidden">
            <div className="animate-marquee-ltr flex gap-8 items-center px-4 w-max">
              {/* Duplicate list to create seamless loop (12 sets to ensure coverage on wide screens even with few items) */}
              {[...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems, ...allItems].map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  onClick={() => navigate('/menu')}
                  className="relative group cursor-pointer transition-transform hover:scale-110"
                  title={item.name} // Native tooltip for accessibility
                >
                  {item.image ? (
                    <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-r from-gold-500/20 to-gold-500/0 border border-gold-500/30 group-hover:border-gold-500 transition-all shadow-lg overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full rounded-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-900 border border-white/10 flex items-center justify-center group-hover:border-gold-500 transition-all">
                      <Utensils className="w-8 h-8 text-gray-600 group-hover:text-gold-500" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Events Section */}
      {events.length > 0 && (
        <section className="py-24 relative z-10 overflow-hidden">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-2 block animate-pulse">Happening Now</span>
              <h2 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 to-gold-600 font-serif">
                Special Events
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative h-[400px] rounded-3xl overflow-hidden border border-gold-500/30 shadow-2xl"
                >
                  {event.image ? (
                    <img
                      src={event.image}
                      alt={event.title}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=1000'; // Fallback
                      }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                      <Sparkles className="w-20 h-20 text-gold-500/20" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90" />

                  <div className="absolute bottom-0 left-0 p-8 w-full">
                    {event.date && (
                      <div className="inline-flex items-center gap-2 bg-gold-500/20 text-gold-400 px-3 py-1 rounded-full text-sm font-bold mb-4 backdrop-blur-md border border-gold-500/20">
                        <div className="w-2 h-2 rounded-full bg-gold-500 animate-pulse" />
                        {new Date(event.date).toLocaleDateString()}
                      </div>
                    )}
                    <h3 className="text-3xl font-bold text-white mb-3 font-serif line-clamp-2">{event.title}</h3>
                    <p className="text-gray-300 line-clamp-3 mb-6">{event.description}</p>
                    <Button
                      className="bg-white/10 hover:bg-gold-500 hover:text-black text-white rounded-full backdrop-blur-md border border-white/20 transition-all font-bold"
                      onClick={() => navigate('/contact')}
                    >
                      Enquire Now
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="py-12 border-y border-white/5 bg-black/50 backdrop-blur-xl relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-5xl font-bold text-gold-500 mb-2 font-serif">{stat.number}</div>
                <div className="text-sm md:text-base text-gray-400 uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 to-gold-600 mb-6 font-serif">
              Why Choose Magic World?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We're committed to delivering the best food experience with quality, speed, and care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl hover:border-gold-500/50 transition-all duration-300 hover:-translate-y-2 group hover:shadow-neon-gold"
              >
                <div className="mb-6 bg-black/40 w-16 h-16 rounded-2xl flex items-center justify-center border border-white/10 group-hover:bg-gold-500 group-hover:text-black transition-all duration-300 shadow-lg">
                  <div className="group-hover:text-black text-gold-500 transition-colors">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold-400 transition-colors">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900/50 relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 to-gold-600 mb-6 font-serif">
              Featured Dishes
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Try our most popular and delicious items
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredItems.map((item, index) => (
              <motion.div
                key={item.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate('/menu')}
                className="group cursor-pointer"
              >
                <Card className="glass-card border-none overflow-hidden h-full group hover:shadow-neon-gold transition-all duration-500">
                  <div className="relative h-72 overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <Utensils className="w-12 h-12 text-gray-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />

                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1 drop-shadow-md">{item.name}</h3>
                      <div className="flex justify-between items-center">
                        <span className="text-gold-400 font-bold text-lg">Rs. {item.price}</span>
                        <Button size="sm" variant="ghost" className="text-white hover:text-gold-400 hover:bg-white/10 rounded-full p-2">
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/menu')}
              variant="outline"
              className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black px-8 py-6 rounded-full text-lg"
            >
              View Full Menu <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/3">
              <h2 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 to-gold-600 mb-6 font-serif">
                What Our Customers Say
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Don't just take our word for it - hear from our happy customers who have experienced the magic of our food.
              </p>
              <Button
                onClick={() => navigate('/reviews')}
                className="bg-gold-500 text-black hover:bg-gold-400 rounded-full"
              >
                Read All Reviews
              </Button>
            </div>

            <div className="md:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.length > 0 ? reviews.map((review, index) => (
                <motion.div
                  key={review.id || index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-900/30 p-8 rounded-2xl border border-white/5 backdrop-blur-sm relative"
                >
                  <Quote className="absolute top-6 right-6 text-gold-500/20 w-8 h-8" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-gold-500 fill-gold-500" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic line-clamp-3">"{review.comment}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                      <User className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{review.name}</p>
                      <p className="text-xs text-gray-500">Verified Customer</p>
                    </div>
                  </div>
                </motion.div>
              )) : (
                <div className="col-span-full text-center py-12 bg-white/5 rounded-2xl">
                  <p className="text-gray-400">No reviews yet. Be the first to share your experience!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section id="about" className="py-24 bg-gradient-to-t from-black to-gray-900/50 relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 to-gold-600 mb-8 font-serif">
                About Magic World
              </h2>
              <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                Magic World is your premium cloud kitchen, dedicated to delivering authentic,
                delicious food right to your doorstep. We believe that great food creates magical moments.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center text-gold-500">
                    <ChefHat className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">Expert Chefs</h4>
                    <p className="text-sm text-gray-500">Experienced culinary artists</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center text-gold-500">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">Hygiene First</h4>
                    <p className="text-sm text-gray-500">100% clean and safe environment</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => navigate('/about')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Learn More About Us <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gold-500/20 blur-3xl rounded-full" />
              <img
                src="/about-burger.png"
                alt="About Magic World"
                className="relative z-10 w-full max-w-md mx-auto rounded-2xl shadow-2xl border border-white/10 rotate-3 hover:rotate-0 transition-transform duration-500 shadow-neon-gold"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-gold-900/20 to-purple-900/20 p-12 rounded-3xl border border-gold-500/20 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 w-full h-full opacity-5"
              style={{ backgroundImage: `url(${NoiseBg})` }}
            />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif relative z-10"
            >
              Ready to Experience the Magic?
            </motion.h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto relative z-10">
              Order now and get your favorite dishes delivered fresh and hot to your doorstep.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center relative z-10">
              <Button
                onClick={() => navigate('/menu')}
                size="lg"
                className="bg-gold-500 text-black hover:bg-gold-400 px-10 rounded-full font-bold text-lg h-14"
              >
                Start Ordering
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white/10 px-10 rounded-full font-bold text-lg h-14"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;



